import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import BundleCostEstimate from './BundleCostEstimate';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  LinkIcon,
  SparklesIcon,
  NoSymbolIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const CLIENT_URL = process.env.REACT_APP_CLIENT_URL || 'http://localhost:3000';

function bosWorkspaceHref(accountId) {
  return `${CLIENT_URL}/crm/${accountId}`;
}

// Canonical order. Dashboard and Settings are always present and are not
// checkboxes. Quotations is its own module and starts off on a new bundle.
const BOS_MODULE_OPTIONS = [
  { key: 'leads', label: 'Leads' },
  { key: 'omni', label: 'Omni Agent' },
  { key: 'customers', label: 'Customers' },
  { key: 'quotations', label: 'Quotations' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'automations', label: 'Automations' },
  { key: 'reports', label: 'Reports' },
  { key: 'auditLog', label: 'Audit log' },
];
const BOS_MODULE_KEYS = BOS_MODULE_OPTIONS.map((option) => option.key);
const DEFAULT_NEW_BOS_MODULES = ['automations', 'reports', 'auditLog'];

function normalizeBosDraft(list) {
  const selected = new Set(Array.isArray(list) ? list : []);
  return BOS_MODULE_KEYS.filter((key) => selected.has(key));
}

function bosDraftFromBundle(existing, productComponents) {
  const products = Array.isArray(productComponents) ? productComponents : [];
  if (!existing) return normalizeBosDraft(DEFAULT_NEW_BOS_MODULES);
  if (Array.isArray(existing.bos_modules)) {
    // Same rule as a null list: the inbox stays off unless the product is
    // already on, so a glance-and-save cannot start WhatsApp. A list that
    // omitted the inbox stays unchecked even when the product is on, so that
    // save does not turn the inbox on. componentsForBosSale keeps the product.
    const listed = existing.bos_modules.filter((key) => key !== 'omni' || products.includes('omni'));
    return normalizeBosDraft(listed);
  }
  // A null list is the legacy "everything on" bundle. Showing every module
  // checked means a glance-and-save cannot strip Leads or Quotations.
  // Omni stays off unless the product is already on, so that same save does
  // not start a WhatsApp agent the account never had.
  const legacy = BOS_MODULE_KEYS.filter((key) => key !== 'omni' || products.includes('omni'));
  return normalizeBosDraft(legacy);
}

// This card sells BOS. A website or newsletter already stored stays.
// The Omni Agent checkbox is the inbox. Checking it also writes the omni
// product so the inbox can turn on. A product already stored stays when the
// inbox is off: inbound WhatsApp and voice follow the product, and most
// existing Omni sales are product-on with the inbox left off.
function componentsForBosSale(stored, bosModules) {
  const source = Array.isArray(stored) ? stored : [];
  const kept = ['site', 'newsletter', 'omni'].filter((key) => source.includes(key));
  const selected = new Set(Array.isArray(bosModules) ? bosModules : []);
  const next = ['crm', ...kept];
  if (selected.has('omni') && !next.includes('omni')) next.push('omni');
  return next;
}

const STATUS_STYLES = {
  active: 'bg-green-50 text-green-700 border-green-200',
  pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
  past_due: 'bg-orange-50 text-orange-700 border-orange-200',
  expired: 'bg-gray-50 text-gray-600 border-gray-200',
  canceled: 'bg-red-50 text-red-700 border-red-200',
};

const STATUS_LABELS = {
  active: 'Active',
  pending_payment: 'Awaiting payment',
  past_due: 'Past due',
  expired: 'Expired',
  canceled: 'Canceled',
};

function currencySymbol(currency) {
  return String(currency || '').toUpperCase() === 'ILS' ? '₪' : '$';
}

function formatMoney(amount, currency) {
  if (amount === null || amount === undefined) return '—';
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  return `${currencySymbol(currency)}${value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function parseCustomAmountInput(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return { value: null };
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return { error: 'Enter a custom price with at most two decimal places' };
  }
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0.01 || value > 99999999.99) {
    return { error: 'Enter a custom price between 0.01 and 99,999,999.99' };
  }
  return { value };
}

function catalogCreditsForTier(tiers, tierKey) {
  const match = (tiers || []).find((entry) => entry.key === tierKey);
  const credits = match?.credits != null ? Number(match.credits) : null;
  return Number.isFinite(credits) ? credits : null;
}

function tierKeyForCredits(credits, tiers) {
  const count = Number(credits);
  if (!Number.isFinite(count) || count <= 0) return null;
  const ordered = [...(tiers || [])].sort((a, b) => Number(a.credits) - Number(b.credits));
  const covering = ordered.find((entry) => Number(entry.credits) >= count);
  return (covering || ordered[ordered.length - 1] || {}).key || null;
}

function GrantRow({
  label,
  amountLabel,
  cadenceLabel,
  amount,
  cadence,
  onAmount,
  onCadence,
  onSubmit,
  saving,
  icon: Icon,
  balance,
  monthlyAmount,
  help,
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
          {label}
        </label>
        <input
          type="number"
          min="0"
          aria-label={amountLabel}
          value={amount}
          onChange={(e) => onAmount(e.target.value)}
          placeholder="500"
          className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
          When
        </label>
        <select
          aria-label={cadenceLabel}
          value={cadence}
          onChange={(e) => onCadence(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="once">One time</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>
      <button
        type="button"
        onClick={onSubmit}
        disabled={saving}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
      >
        <Icon className="h-4 w-4" />
        {cadence === 'monthly' ? 'Save monthly grant' : 'Grant once'}
      </button>
      <div className="text-xs text-gray-500 max-w-xl">
        <div>{balance}{monthlyAmount > 0 ? ` · ${monthlyAmount.toLocaleString()} / month` : ''}</div>
        <div className="mt-0.5">{help}</div>
      </div>
    </div>
  );
}

function OneTimeGrantEditor({ grant, noun, saving, onSave, onDelete }) {
  const [draft, setDraft] = useState(String(grant.amount));
  const created = grant.createdAt
    ? new Date(grant.createdAt).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    })
    : '';
  const expires = grant.expiresAt
    ? new Date(grant.expiresAt).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    })
    : '';
  const showLeft = grant.remaining != null && Number(grant.remaining) !== Number(grant.amount);

  return (
    <li className="flex flex-wrap items-center gap-2">
      <input
        type="number"
        min="1"
        aria-label={`Change one-time ${noun} grant ${grant.id}`}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        className="w-28 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
      />
      <button
        type="button"
        onClick={() => onSave(grant, draft)}
        disabled={saving}
        className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
      >
        Save
      </button>
      <button
        type="button"
        onClick={() => onDelete(grant)}
        disabled={saving}
        aria-label={`Delete one-time ${noun} grant ${grant.id}`}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-200 text-red-700 text-sm font-medium hover:bg-red-50 disabled:opacity-50"
      >
        <TrashIcon className="h-4 w-4" />
        Delete
      </button>
      <span className="text-xs text-gray-500">
        {created}
        {showLeft ? ` · ${Number(grant.remaining).toLocaleString()} left` : ''}
        {expires ? ` · expires ${expires}` : ''}
      </span>
    </li>
  );
}

function OneTimeGrantList({ noun, grants, saving, onSave, onDelete }) {
  if (!grants?.length) return null;
  return (
    <ul className="mt-3 space-y-2">
      {grants.map((grant) => (
        <OneTimeGrantEditor
          key={`${grant.id}:${grant.amount}`}
          grant={grant}
          noun={noun}
          saving={saving}
          onSave={onSave}
          onDelete={onDelete}
        />
      ))}
    </ul>
  );
}

/**
 * Engagement Bundle management for one account.
 *
 * Renders nothing at all when the feature is off server-side (the routes 404),
 * so the backoffice needs no separate flag plumbing — a 404 on load simply
 * hides the card.
 */
const BundleSection = ({ accountId }) => {
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [data, setData] = useState(null);

  // Draft state for the activation form. Credits and both prices stay empty
  // until sales types them — a catalog list price must not land in the fields.
  const [monthlyCredits, setMonthlyCredits] = useState('');
  const [initialAmount, setInitialAmount] = useState('');
  const [recurringAmount, setRecurringAmount] = useState('');
  const [components, setComponents] = useState(['crm']);
  const [bosModuleDraft, setBosModuleDraft] = useState(DEFAULT_NEW_BOS_MODULES);
  // Customers can open the design studio agent unless sales turns this off.
  const [studioAgentOpen, setStudioAgentOpen] = useState(true);
  const [currency, setCurrency] = useState('USD');
  const [notes, setNotes] = useState('');
  const [grantCredits, setGrantCredits] = useState('');
  const [creditCadence, setCreditCadence] = useState('once');
  const [grantMinutes, setGrantMinutes] = useState('');
  const [minuteCadence, setMinuteCadence] = useState('once');
  const [grantTemplates, setGrantTemplates] = useState('');
  const [templateCadence, setTemplateCadence] = useState('once');
  const [externalPaymentNote, setExternalPaymentNote] = useState('');
  const [recordedAmount, setRecordedAmount] = useState('');
  const [recordedInitialAmount, setRecordedInitialAmount] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catalogRes, detailRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/bundles/tiers`),
        axios.get(`${API_BASE_URL}/api/admin/bundles/${accountId}`),
      ]);
      setCatalog(catalogRes.data);
      setData(detailRes.data);

      const existing = detailRes.data?.bundle;
      let loadedCredits = '';
      let loadedInitial = '';
      let loadedRecurring = '';
      if (existing) {
        setCurrency(existing.currency || 'USD');
        setNotes(existing.notes || '');
        setComponents(Array.isArray(existing.components) ? existing.components : ['crm']);
        setBosModuleDraft(bosDraftFromBundle(existing, existing.components));
        setStudioAgentOpen(existing.studio_agent_open !== false);
        if (existing.included_credits != null && existing.included_credits !== '') {
          loadedCredits = String(existing.included_credits);
        } else if (existing.status === 'active') {
          const catalogCredits = catalogCreditsForTier(catalogRes.data?.tiers, existing.tier);
          loadedCredits = catalogCredits != null ? String(catalogCredits) : '';
        }
        loadedInitial = existing.initial_amount != null && existing.initial_amount !== ''
          ? String(existing.initial_amount)
          : '';
        const storedRecurring = existing.amount != null && existing.amount !== ''
          ? Number(existing.amount)
          : null;
        loadedRecurring = storedRecurring != null && Number.isFinite(storedRecurring)
          ? String(storedRecurring)
          : '';
        setMonthlyCredits(loadedCredits);
        setInitialAmount(loadedInitial);
        setRecurringAmount(loadedRecurring);
        if (existing.activation_method === 'manual' && !existing.payment_reference) {
          setRecordedAmount(loadedRecurring);
          setRecordedInitialAmount(loadedInitial);
        }
      } else {
        setComponents(['crm']);
        setNotes('');
        setBosModuleDraft(DEFAULT_NEW_BOS_MODULES);
        setStudioAgentOpen(true);
        setMonthlyCredits('');
        setInitialAmount('');
        setRecurringAmount('');
      }
      setAvailable(true);
    } catch (error) {
      if (error.response?.status === 404) {
        // Feature flag is off for this environment — hide the card entirely.
        setAvailable(false);
      } else {
        console.error('Error loading bundle:', error);
        toast.error('Failed to load bundle');
      }
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  // Draft only — a leftover invoice reference must not follow an account switch.
  // Kept out of load() so Save custom price (which reloads the card) does not
  // discard a note typed beside the amount before Mark as paid.
  useEffect(() => {
    setExternalPaymentNote('');
  }, [accountId]);

  const toggleBosModule = (key) => {
    setBosModuleDraft((prev) => (
      prev.includes(key) ? prev.filter((moduleKey) => moduleKey !== key) : [...prev, key]
    ));
  };

  const bosModulesForSale = () => normalizeBosDraft(bosModuleDraft);
  const componentsForSale = () => componentsForBosSale(components, bosModuleDraft);

  const handleCreateLink = async (linkOverride) => {
    // A click handler receives the event. Only an explicit override (the
    // active-bundle link) may name the tier that gets billed.
    const savedPlan = Boolean(linkOverride && typeof linkOverride.linkTier === 'string');
    const linkTier = savedPlan
      ? linkOverride.linkTier
      : tierKeyForCredits(monthlyCredits, catalog?.tiers);
    const linkComponents = savedPlan ? linkOverride.linkComponents : componentsForSale();
    const linkNotes = savedPlan ? linkOverride.linkNotes : (notes || null);
    const billingPeriod = savedPlan ? (linkOverride.linkBillingPeriod || 'monthly') : 'monthly';
    const linkCurrency = savedPlan ? (linkOverride.linkCurrency || currency) : currency;
    const linkProvider = savedPlan ? (linkOverride.linkProvider || 'icount') : 'icount';
    const linkAmount = savedPlan ? linkOverride.linkAmount : null;
    if (!Array.isArray(linkComponents) || linkComponents.length === 0) {
      toast.error('Select at least one component');
      return;
    }
    if (savedPlan && (linkAmount == null || !Number.isFinite(Number(linkAmount)))) {
      toast.error('Set the recurring monthly payment before creating a payment link');
      return;
    }
    const accountName = data?.account?.name || 'this account';
    const ownerEmail = data?.owner?.email || 'the account owner';
    const amountLabel = linkAmount != null ? formatMoney(linkAmount, linkCurrency) : '';
    // Kept as a braced placeholder so the consent sentence stays greppable
    // and the period the admin confirms is the one that will be billed.
    const recurringConsent = 'This authorizes an ongoing {billingPeriod} subscription until canceled.'
      .replace('{billingPeriod}', billingPeriod);
    const confirmed = window.confirm(
      `Create a ${linkTier} ${billingPeriod} ${linkCurrency} payment link for:\n\n`
      + `  ${accountName}\n  ${ownerEmail}\n\n`
      + (amountLabel ? `Amount: ${amountLabel} ${linkCurrency}.\n\n` : '')
      + 'Whoever pays this link activates THIS account. '
      + 'A leftover URL from the previous account will upgrade the wrong customer. '
      + recurringConsent
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/admin/bundles/${accountId}/payment-link`, {
        tier: linkTier,
        components: linkComponents,
        billingPeriod,
        currency: linkCurrency,
        provider: linkProvider,
        notes: linkNotes,
        ...(savedPlan && linkAmount != null ? { amount: Number(linkAmount) } : {}),
        // The saved-plan link bills the live plan and must not rewrite the
        // module list. A new sale stores the checkboxes on the pending row.
        ...(!savedPlan && linkComponents.includes('crm')
          ? { bosModules: bosModulesForSale(), studioAgentOpen }
          : {}),
      });
      toast.success('Payment link created');
      await navigator.clipboard?.writeText(res.data.paymentUrl).catch(() => {});
      await load();
    } catch (error) {
      console.error('Error creating payment link:', error);
      toast.error(error.response?.data?.message || 'Failed to create payment link');
    } finally {
      setSaving(false);
    }
  };

  // An active bundle's credit field is a draft until Update credits is
  // confirmed. The payment link must bill the amount already on the account.
  const handleCreateSavedPlanLink = () => {
    const existing = data?.bundle;
    const stored = existing?.amount != null && existing.amount !== '' ? Number(existing.amount) : null;
    return handleCreateLink({
      linkTier: existing?.tier,
      linkAmount: stored != null && Number.isFinite(stored) ? stored : null,
      linkCurrency: existing?.currency || 'USD',
      linkBillingPeriod: 'monthly',
      linkProvider: 'icount',
      linkComponents: Array.isArray(existing?.components) ? existing.components : [],
      linkNotes: existing?.notes || null,
    });
  };

  const handleSaveCustomPrice = async () => {
    const parsed = parseCustomAmountInput(recordedAmount);
    if (parsed.error) {
      toast.error(parsed.error);
      return;
    }
    const first = parseCustomAmountInput(recordedInitialAmount);
    if (first.error) {
      toast.error(first.error);
      return;
    }
    if (parsed.value == null) {
      toast.error('Enter a custom price');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/bundles/${accountId}/custom-price`, {
        amount: parsed.value,
        initialAmount: first.value,
        currency,
      });
      toast.success('Custom price saved');
      await load();
    } catch (error) {
      console.error('Error saving custom bundle price:', error);
      toast.error(error.response?.data?.message || 'Failed to save custom price');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaidOutOfBand = async () => {
    const parsed = parseCustomAmountInput(recordedAmount);
    if (parsed.error) {
      toast.error(parsed.error);
      return;
    }
    const first = parseCustomAmountInput(recordedInitialAmount);
    if (first.error) {
      toast.error(first.error);
      return;
    }
    const saleCurrency = data?.bundle?.currency || currency;
    const firstLine = first.value != null
      ? `The first payment on this account will be ${formatMoney(first.value, saleCurrency)}. `
      : 'The first payment already on this account stays as it is. ';
    const priceLine = parsed.value != null
      ? `The recurring monthly price on this account will be ${formatMoney(parsed.value, saleCurrency)}.`
      : 'The recurring monthly price already on this account stays as it is.';
    const confirmed = window.confirm(
      'Mark this bundle as paid outside iCount and PayPal?\n\n'
      + 'This records that the invoice was collected out of band. '
      + 'The plan, credits, and billing period stay as they are. '
      + `${firstLine}${priceLine} `
      + 'Automatic renewal stays off until a recurring payment link is paid.'
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/bundles/${accountId}/mark-paid`, {
        note: externalPaymentNote.trim() || null,
        amount: parsed.value,
        initialAmount: first.value,
      });
      toast.success('Marked as paid');
      setExternalPaymentNote('');
      await load();
    } catch (error) {
      console.error('Error marking bundle paid:', error);
      toast.error(error.response?.data?.message || 'Failed to mark bundle as paid');
    } finally {
      setSaving(false);
    }
  };

  const handleManualActivate = async () => {
    const credits = parseInt(String(monthlyCredits).trim(), 10);
    if (!Number.isInteger(credits) || credits < 1) {
      toast.error('Enter a whole number of monthly credits');
      return;
    }
    const first = parseCustomAmountInput(initialAmount);
    if (first.error) {
      toast.error(first.error);
      return;
    }
    const recurring = parseCustomAmountInput(recurringAmount);
    if (recurring.error) {
      toast.error(recurring.error);
      return;
    }
    if (first.value == null || recurring.value == null) {
      toast.error('Enter both the first payment and the recurring monthly payment');
      return;
    }
    const saleCurrency = String(currency || '').toUpperCase() === 'ILS' ? 'ILS' : 'USD';
    const confirmed = window.confirm(
      `Turn this account on with ${credits.toLocaleString()} credits per month?\n\n`
      + `First payment: ${formatMoney(first.value, saleCurrency)}.\n`
      + `Then ${formatMoney(recurring.value, saleCurrency)} every month.\n`
      + `Currency: ${saleCurrency}.\n\n`
      + 'An account that was already activated keeps its billing date and credits. '
      + 'A first activation starts a period today and opens BOS. '
      + 'The action is logged against your admin user.'
    );
    if (!confirmed) return;

    // Open during this click. A window.open after the activate and reload
    // awaits is outside the user gesture, and browsers block that popup.
    // The tab stays blank until the grant has landed, then loads BOS.
    const bosTab = window.open('about:blank', '_blank');

    setSaving(true);
    let activated = false;
    try {
      await axios.post(`${API_BASE_URL}/api/admin/bundles/${accountId}/activate`, {
        credits,
        initialAmount: first.value,
        recurringAmount: recurring.value,
        currency: saleCurrency,
        components: componentsForSale(),
        billingPeriod: 'monthly',
        notes: notes || null,
        bosModules: bosModulesForSale(),
        studioAgentOpen,
      });
      toast.success('Bundle activated');
      await load();
      activated = true;
    } catch (error) {
      if (bosTab && !bosTab.closed) bosTab.close();
      console.error('Error activating bundle:', error);
      toast.error(error.response?.data?.message || 'Failed to activate bundle');
    } finally {
      setSaving(false);
    }
    if (!activated || !bosTab || bosTab.closed) return;
    try {
      bosTab.location.replace(bosWorkspaceHref(accountId));
      bosTab.opener = null;
    } catch (_) {
      // The grant already succeeded. Open BOS on the card still works.
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/bundles/${accountId}`, {
        components: componentsForSale(),
        notes: notes || null,
        // Pending, expired, and canceled rows edit modules on this form.
        // A later payment webhook omits bosModules, so COALESCE keeps
        // whatever this save wrote.
        bosModules: bosModulesForSale(),
        studioAgentOpen,
      });
      toast.success('Bundle updated');
      await load();
    } catch (error) {
      console.error('Error updating bundle:', error);
      toast.error(error.response?.data?.message || 'Failed to update bundle');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBosModules = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/bundles/${accountId}`, {
        components: componentsForSale(),
        bosModules: bosModulesForSale(),
        studioAgentOpen,
      });
      toast.success('BOS modules updated');
      await load();
    } catch (error) {
      console.error('Error updating BOS modules:', error);
      toast.error(error.response?.data?.message || 'Failed to update BOS modules');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCredits = async () => {
    const existing = data?.bundle;
    const credits = parseInt(String(monthlyCredits).trim(), 10);
    if (!existing || !Number.isInteger(credits) || credits < 1) return;
    const current = existing.included_credits != null && existing.included_credits !== ''
      ? Number(existing.included_credits)
      : catalogCreditsForTier(catalog?.tiers, existing.tier);
    if (current != null && credits === Number(current)) return;
    const name = data?.account?.name || 'this account';
    const lines = [
      `Change ${name} to ${credits.toLocaleString()} credits per month?`,
      '',
      'The allowance updates immediately.',
    ];
    if (existing.status === 'active') {
      lines.push(
        `Monthly credits reset to ${credits.toLocaleString()}. `
        + 'Credits already used this period go back to 0. Top-up credits stay.'
      );
    }
    lines.push('The billed price and the payment subscription stay as they are.');
    if (!window.confirm(lines.join('\n'))) {
      setMonthlyCredits(current != null ? String(current) : '');
      return;
    }

    setSaving(true);
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/bundles/${accountId}`, {
        includedCredits: credits,
      });
      toast.success(`${name} now includes ${credits.toLocaleString()} credits per month`);
      await load();
    } catch (error) {
      console.error('Error updating monthly credits:', error);
      toast.error(error.response?.data?.message || 'Failed to update credits');
      setMonthlyCredits(current != null ? String(current) : '');
    } finally {
      setSaving(false);
    }
  };

  const postGrant = async ({ path, body, confirmText, successText, onDone, method = 'post' }) => {
    if (!window.confirm(confirmText)) return null;
    setSaving(true);
    try {
      const response = await axios({
        method,
        url: `${API_BASE_URL}/api/admin/bundles/${accountId}/${path}`,
        ...(method === 'delete' ? {} : { data: body }),
      });
      const message = typeof successText === 'function' ? successText(response.data) : successText;
      if (message) toast.success(message);
      if (onDone) onDone();
      await load();
      return response.data;
    } catch (error) {
      console.error(`Error saving ${path}:`, error);
      toast.error(error.response?.data?.message || 'Failed to save grant');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleGrantCredits = async () => {
    const amount = parseInt(grantCredits, 10);
    const monthly = creditCadence === 'monthly';
    if (!Number.isInteger(amount) || amount < 0 || (monthly && amount > 100000) || (!monthly && amount <= 0)) {
      toast.error(monthly ? 'Enter a whole number from 0 to 100000' : 'Enter a positive number of credits');
      return;
    }
    const confirmText = monthly
      ? (amount === 0
        ? 'Turn off the monthly credit grant? The sold monthly allowance stays as it is.'
        : `Set a monthly grant of ${amount.toLocaleString()} credits? It is added on top of the sold allowance this period and every later period. Unused grant resets with the allowance. This replaces the previous monthly grant.`)
      : `Grant ${amount.toLocaleString()} credits once? They never expire and are spent after the monthly allowance.`;
    await postGrant({
      path: 'credits',
      body: { credits: amount, reason: 'backoffice_grant', cadence: monthly ? 'monthly' : 'once' },
      confirmText,
      successText: monthly
        ? (amount === 0 ? 'Monthly credit grant turned off' : `Monthly credit grant set to ${amount.toLocaleString()}`)
        : `Granted ${amount.toLocaleString()} credits`,
      onDone: () => setGrantCredits(''),
    });
  };

  const handleGrantMinutes = async () => {
    const amount = parseInt(grantMinutes, 10);
    const monthly = minuteCadence === 'monthly';
    if (!Number.isInteger(amount) || amount < 0 || amount > 100000 || (!monthly && amount < 1)) {
      toast.error(monthly ? 'Enter a whole number from 0 to 100000' : 'Enter a whole number from 1 to 100000');
      return;
    }
    const confirmText = monthly
      ? (amount === 0
        ? 'Turn off the monthly call-minute grant? Months already granted stay until they expire.'
        : `Save ${amount.toLocaleString()} call minutes every month? This month is granted now and expires at the end of the month (Asia/Jerusalem). Later months are granted on the 1st. A month already granted is not granted again.`)
      : `Grant ${amount.toLocaleString()} call minutes once? They last 12 months.`;
    await postGrant({
      path: 'voice-minutes',
      body: { minutes: amount, cadence: monthly ? 'monthly' : 'once' },
      confirmText,
      successText: monthly
        ? (amount === 0 ? 'Monthly call-minute grant turned off' : `Monthly call minutes set to ${amount.toLocaleString()}`)
        : `Granted ${amount.toLocaleString()} call minutes`,
      onDone: () => setGrantMinutes(''),
    });
  };

  const handleGrantTemplates = async () => {
    const amount = parseInt(grantTemplates, 10);
    const monthly = templateCadence === 'monthly';
    if (!Number.isInteger(amount) || amount < 0 || amount > 100000 || (!monthly && amount < 1)) {
      toast.error(monthly ? 'Enter a whole number from 0 to 100000' : 'Enter a whole number from 1 to 100000');
      return;
    }
    const confirmText = monthly
      ? (amount === 0
        ? 'Turn off the monthly WhatsApp template grant? Months already granted stay until they expire.'
        : `Save ${amount.toLocaleString()} WhatsApp template credits every month? This month is granted now and expires at the end of the month (Asia/Jerusalem). Later months are granted on the 1st. A month already granted is not granted again.`)
      : `Grant ${amount.toLocaleString()} WhatsApp template credits once? They last 12 months.`;
    await postGrant({
      path: 'whatsapp-templates',
      body: { credits: amount, cadence: monthly ? 'monthly' : 'once' },
      confirmText,
      successText: monthly
        ? (amount === 0 ? 'Monthly WhatsApp template grant turned off' : `Monthly WhatsApp templates set to ${amount.toLocaleString()}`)
        : `Granted ${amount.toLocaleString()} WhatsApp template credits`,
      onDone: () => setGrantTemplates(''),
    });
  };

  const handleChangeOneTimeGrant = async ({ path, field, noun, grant, next }) => {
    const amount = parseInt(next, 10);
    if (!Number.isInteger(amount) || amount < 1 || amount > 100000) {
      toast.error('Enter a whole number from 1 to 100000');
      return;
    }
    if (amount === Number(grant.amount)) return;
    await postGrant({
      method: 'patch',
      path: `${path}/${grant.id}`,
      body: { [field]: amount },
      confirmText: `Change this one-time ${noun} grant from ${Number(grant.amount).toLocaleString()} to ${amount.toLocaleString()}?`,
      successText: `One-time ${noun} grant changed to ${amount.toLocaleString()}`,
    });
  };

  const handleDeleteOneTimeGrant = async ({ path, noun, grant }) => {
    await postGrant({
      method: 'delete',
      path: `${path}/${grant.id}`,
      confirmText: `Delete this one-time ${noun} grant of ${Number(grant.amount).toLocaleString()}? Unused ${noun} leave the balance.`,
      successText: (result) => {
        const spent = Number(result?.alreadySpent) || 0;
        const removed = Number(result?.removed) || 0;
        if (spent > 0) {
          return `Removed ${removed.toLocaleString()} unused ${noun}. ${spent.toLocaleString()} were already used.`;
        }
        return `Deleted the one-time ${noun} grant`;
      },
    });
  };

  const handleCancel = async (atPeriodEnd) => {
    const confirmed = window.confirm(
      atPeriodEnd
        ? 'Cancel this bundle at the end of the paid period? The customer keeps access until then.'
        : 'Cancel this bundle IMMEDIATELY? The customer loses access to every component right away, and the Omni Agent WhatsApp number is released.'
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/bundles/${accountId}/cancel`, { atPeriodEnd });
      const omniRelease = response.data?.bundle?.omni_release;
      if (omniRelease?.errors?.length) {
        toast.error('Bundle canceled, but the WhatsApp number could not be released.');
      } else if (omniRelease?.released) {
        toast.success('Bundle canceled and the WhatsApp number was released');
      } else {
        toast.success('Bundle canceled');
      }
      await load();
    } catch (error) {
      console.error('Error canceling bundle:', error);
      toast.error('Failed to cancel bundle');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspendAccess = async (suspend) => {
    const confirmed = window.confirm(
      suspend
        ? 'Suspend access for everyone on this account?\n\n'
          + 'They will not be able to sign in or use the dashboard. '
          + 'The bundle, billing, WhatsApp number, published sites, and saved data all stay as they are. '
          + 'Nothing is deleted. You can restore access later.'
        : 'Restore dashboard access for this account?\n\n'
          + 'People on the account can sign in again. Billing, the WhatsApp number, and saved data stay unchanged.'
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/bundles/${accountId}/${suspend ? 'suspend' : 'resume'}`
      );
      toast.success(suspend ? 'Account access suspended' : 'Account access restored');
      await load();
    } catch (error) {
      console.error('Error updating account access:', error);
      toast.error(error.response?.data?.error || 'Failed to update account access');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    const confirmed = window.confirm(
      'Move this account back to the LEGACY billing model?\n\n'
      + 'Their per-site plans and add-ons take over again and the bundle stops granting access.'
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/bundles/${accountId}/revert-to-legacy`);
      toast.success('Account reverted to legacy billing');
      await load();
    } catch (error) {
      console.error('Error reverting account:', error);
      toast.error('Failed to revert account');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreEnterprise = async () => {
    const confirmed = window.confirm(
      'Restore this account to the ENTERPRISE / Agency plan?\n\n'
      + 'The Engagement Bundle is canceled, billing returns to legacy, and '
      + 'the account tier is set back to Enterprise so Agency Workspace '
      + 'and multi-site creation work again.\n\n'
      + 'Use this when creating a client site accidentally flipped an '
      + 'agency onto Growth.'
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/bundles/${accountId}/revert-to-legacy`, {
        restoreTier: 'enterprise',
      });
      toast.success('Account restored to Enterprise / Agency');
      await load();
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error restoring enterprise account:', error);
      toast.error('Failed to restore Enterprise plan');
    } finally {
      setSaving(false);
    }
  };

  if (!available) return null;

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center gap-2 text-gray-500">
          <ArrowPathIcon className="h-5 w-5 animate-spin" />
          Loading bundle…
        </div>
      </div>
    );
  }

  const bundle = data?.bundle;
  const wallet = data?.wallet;
  const isActive = bundle?.status === 'active';
  const bundleComponents = Array.isArray(bundle?.components) && bundle.components.length
    ? bundle.components
    : components;
  const canOpenBos = Boolean(
    bundle
    && (bundle.status === 'active' || bundle.status === 'past_due')
    && bundleComponents.includes('crm')
  );
  const canRecordExternalPayment = bundle?.activation_method === 'manual'
    && (bundle.status === 'active' || bundle.status === 'past_due')
    && !bundle.payment_reference;
  const savedTierMeta = catalog?.tiers?.find((t) => t.key === bundle?.tier) || null;
  const bundleListPrice = savedTierMeta?.pricing?.[bundle?.currency || 'USD']?.[bundle?.billing_period || 'monthly'] ?? null;
  const billedAmount = bundle?.amount != null && bundle.amount !== '' ? Number(bundle.amount) : null;
  const recurringStored = billedAmount != null && Number.isFinite(billedAmount) ? billedAmount : null;
  const currentAllowance = bundle?.included_credits != null && bundle.included_credits !== ''
    ? Number(bundle.included_credits)
    : (savedTierMeta?.credits != null ? Number(savedTierMeta.credits) : null);
  const creditDraft = parseInt(String(monthlyCredits).trim(), 10);
  const creditsUnchanged = Number.isInteger(creditDraft)
    && currentAllowance != null
    && creditDraft === Number(currentAllowance);
  const showsCustomPrice = billedAmount != null
    && bundleListPrice != null
    && bundle?.included_credits == null
    && Math.abs(billedAmount - Number(bundleListPrice)) > 0.001;
  const hasInitialAmount = bundle?.initial_amount != null && bundle.initial_amount !== '';
  const bosModulePicker = (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
        BOS modules
      </div>
      <div className="flex flex-wrap gap-2">
        {BOS_MODULE_OPTIONS.map((option) => {
          const selected = bosModuleDraft.includes(option.key);
          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={selected}
              disabled={saving}
              onClick={() => toggleBosModule(option.key)}
              className={`px-3 py-1.5 rounded-full border text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                selected
                  ? 'border-primary-500 bg-primary-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Dashboard and Settings stay. Quotations is selected on its own.
        Checking Omni Agent includes the WhatsApp agent.
        A website or newsletter already on this account stays; this form does not add either.
        Turning a module off hides it; the data comes back when it is turned on again.
      </p>
      <div className="mt-4">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
          Design Studio
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={studioAgentOpen}
          aria-label="Design Studio agent opening"
          disabled={saving}
          onClick={() => setStudioAgentOpen((open) => !open)}
          className={`px-3 py-1.5 rounded-full border text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
            studioAgentOpen
              ? 'border-primary-500 bg-primary-600 text-white'
              : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
          }`}
        >
          {studioAgentOpen ? 'Opening on' : 'Opening off'}
        </button>
        <p className="mt-2 text-xs text-gray-500">
          When this is on, the customer can open the design studio agent from BOS settings.
          It stays on unless you turn it off. A design already published stays visible either way.
        </p>
      </div>
    </div>
  );
  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Engagement Bundle</h2>
          {bundle && (
            <span className={`ml-2 px-2 py-0.5 rounded-full border text-xs font-medium ${STATUS_STYLES[bundle.status] || STATUS_STYLES.expired}`}>
              {STATUS_LABELS[bundle.status] || bundle.status}
            </span>
          )}
          {!bundle && (
            <span className="ml-2 px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 text-gray-600 text-xs font-medium">
              Legacy billing
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canOpenBos && (
            <a
              href={bosWorkspaceHref(accountId)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
            >
              Open BOS
            </a>
          )}
          <button
            onClick={load}
            className="text-gray-400 hover:text-gray-600"
            title="Refresh"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Live state */}
        {bundle && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                {bundle.included_credits != null ? 'Monthly credits' : 'Tier'}
              </div>
              <div className="mt-1 text-lg font-semibold text-gray-900">
                {bundle.included_credits != null
                  ? `${Number(bundle.included_credits).toLocaleString()} / mo`
                  : (catalog?.tiers?.find((t) => t.key === bundle.tier)?.displayName || bundle.tier)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {hasInitialAmount
                  ? `First ${formatMoney(bundle.initial_amount, bundle.currency)} · then ${formatMoney(bundle.amount, bundle.currency)} / mo`
                  : `${formatMoney(bundle.amount, bundle.currency)} / ${bundle.billing_period}`}
                {showsCustomPrice ? ' · custom' : ''}
              </div>
              {showsCustomPrice && (
                <div className="text-xs text-gray-400 mt-0.5">
                  List {formatMoney(bundleListPrice, bundle.currency)}
                </div>
              )}
              {Number(wallet?.monthlyGrantCredits) > 0 && (
                <div className="text-xs text-gray-500 mt-0.5">
                  +{Number(wallet.monthlyGrantCredits).toLocaleString()} monthly grant
                </div>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Credits this period</div>
              <div className="mt-1 text-lg font-semibold text-gray-900">
                {wallet ? `${wallet.monthlyUsed} / ${wallet.monthlyLimit}` : '—'}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {wallet?.topUpRemaining ? `+${wallet.topUpRemaining} top-up` : 'No top-up balance'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Period ends</div>
              <div className="mt-1 text-lg font-semibold text-gray-900">
                {formatDate(bundle.period_end)}
              </div>
              {bundle.cancel_at_period_end && (
                <div className="text-xs text-red-600 mt-1">Cancels at period end</div>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Activated</div>
              <div className="mt-1 text-lg font-semibold text-gray-900">
                {formatDate(bundle.activated_at)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {bundle.activation_method === 'manual'
                  ? (bundle.external_paid_at ? 'Manual · paid outside the system' : 'Manual (no payment record)')
                  : 'Payment link'}
              </div>
            </div>
          </div>
        )}

        {bundle?.notes && (
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-3">
            <div className="text-xs uppercase tracking-wide text-primary-700 font-medium">
              Sales notes
            </div>
            <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap">{bundle.notes}</p>
          </div>
        )}

        <BundleCostEstimate key={accountId} accountId={accountId} />

        {canRecordExternalPayment && (
          <div className={`rounded-lg border p-3 text-sm space-y-3 ${
            bundle.external_paid_at
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
          >
            {bundle.external_paid_at ? (
              <div className="flex items-start gap-2">
                <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p>
                    Marked paid outside the system on {formatDate(bundle.external_paid_at)}.
                    Automatic renewal stays off until a recurring payment link is paid.
                  </p>
                  {hasInitialAmount && (
                    <p className="mt-1 text-green-900">
                      First payment {formatMoney(bundle.initial_amount, bundle.currency)}, then {formatMoney(bundle.amount, bundle.currency)} / month.
                    </p>
                  )}
                  {bundle.external_payment_note && (
                    <p className="mt-1 text-green-900">Reference: {bundle.external_payment_note}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                <span>
                  This bundle was activated manually. There is no payment record in iCount or
                  PayPal, and automatic renewal is off. Mark it paid once the invoice is
                  collected outside the system.
                </span>
              </div>
            )}
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${bundle.external_paid_at ? '' : 'sm:pl-7'}`}>
              <div>
                <label htmlFor="recorded-first-payment" className="block text-xs uppercase tracking-wide mb-1">
                  First payment
                </label>
                <input
                  id="recorded-first-payment"
                  aria-label="Recorded first payment"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={recordedInitialAmount}
                  onChange={(e) => setRecordedInitialAmount(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-900"
                />
                <p className="mt-1 text-xs">
                  The amount collected on the first invoice. Leave blank to keep the first payment already stored.
                  Later months use the recurring price.
                </p>
              </div>
              <div>
                <label htmlFor="recorded-custom-price" className="block text-xs uppercase tracking-wide mb-1">
                  Recurring monthly price
                </label>
                <input
                  id="recorded-custom-price"
                  aria-label="Recorded custom price"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={recordedAmount}
                  onChange={(e) => setRecordedAmount(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-900"
                />
                <p className="mt-1 text-xs">
                  Leave blank when marking paid to keep the price already stored.
                  The iCount recurring link charges this amount in the account currency and does not replace it.
                </p>
              </div>
              <div>
                <label htmlFor="recorded-currency" className="block text-xs uppercase tracking-wide mb-1">
                  Currency
                </label>
                <select
                  id="recorded-currency"
                  aria-label="Recorded currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-900"
                >
                  <option value="USD">USD</option>
                  <option value="ILS">ILS</option>
                </select>
              </div>
              {!bundle.external_paid_at && (
                <div>
                  <label htmlFor="external-payment-note" className="block text-xs uppercase tracking-wide text-amber-700 mb-1">
                    Reference (optional)
                  </label>
                  <input
                    id="external-payment-note"
                    aria-label="Out of band payment reference"
                    value={externalPaymentNote}
                    onChange={(e) => setExternalPaymentNote(e.target.value)}
                    maxLength={500}
                    placeholder="Invoice number or bank transfer"
                    className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-900"
                  />
                </div>
              )}
            </div>
            <div className={`flex flex-wrap items-center gap-2 ${bundle.external_paid_at ? '' : 'sm:pl-7'}`}>
              <button
                type="button"
                onClick={handleSaveCustomPrice}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                Save custom price
              </button>
              {!bundle.external_paid_at && (
                <button
                  type="button"
                  onClick={handleMarkPaidOutOfBand}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  Mark as paid
                </button>
              )}
            </div>
          </div>
        )}

        {bundle?.payment_url && !isActive && (
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-primary-900">
              <LinkIcon className="h-4 w-4" />
              Recurring {bundle.billing_period || 'monthly'} payment link (awaiting payment)
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input
                readOnly
                value={bundle.payment_url}
                className="flex-1 text-xs bg-white border border-primary-200 rounded px-2 py-1.5 text-gray-700"
              />
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(bundle.payment_url);
                  toast.success('Copied');
                }}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded bg-primary-600 text-white hover:bg-primary-700"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
                Copy
              </button>
            </div>
          </div>
        )}

        {/* active-bundle-plan */}
        {isActive ? (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Monthly credits</h3>
              <p className="mt-1 text-xs text-gray-500">
                The allowance updates immediately. Monthly credits reset to the new allowance
                and credits already used this period go back to 0. Top-up credits stay.
                The billed price and the payment subscription stay as they are.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="bundle-monthly-credits" className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Credits per month
                </label>
                <input
                  id="bundle-monthly-credits"
                  aria-label="Bundle monthly credits"
                  type="number"
                  min="1"
                  step="1"
                  value={monthlyCredits}
                  onChange={(e) => setMonthlyCredits(e.target.value)}
                  disabled={saving}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                />
              </div>
              <button
                type="button"
                onClick={handleUpdateCredits}
                disabled={saving || !Number.isInteger(creditDraft) || creditsUnchanged}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                Update credits
              </button>
            </div>

            {bundle.activation_method === 'manual' && (
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Recurring payment link</h3>
                  <p className="mt-1 text-xs text-gray-500">
                    {bundle.payment_url
                      ? 'A recurring monthly iCount payment link is ready.'
                      : bundle.external_paid_at
                        ? 'Payment for this period was recorded outside the system. A link starts automatic monthly renewal.'
                        : 'This bundle has no payment record. A link starts automatic monthly renewal.'}
                    {' '}iCount charges the recurring amount already on this account
                    {recurringStored != null ? ` (${formatMoney(recurringStored, bundle.currency)})` : ''}.
                    PayPal cannot bill a negotiated amount.
                  </p>
                </div>
                {bundle.payment_url ? (
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={bundle.payment_url}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 text-xs bg-white border border-gray-300 rounded px-2 py-1.5 text-gray-700"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard?.writeText(bundle.payment_url);
                        toast.success('Copied');
                      }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded bg-primary-600 text-white hover:bg-primary-700"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      Copy
                    </button>
                  </div>
                ) : (
                  <>
                    {recurringStored == null && (
                      <p className="text-xs text-amber-700">
                        Set the recurring monthly payment before creating a link.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={handleCreateSavedPlanLink}
                      disabled={saving || recurringStored == null}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                    >
                      <LinkIcon className="h-4 w-4" />
                      Create recurring payment link
                      {recurringStored != null && (
                        <span className="opacity-80">· {formatMoney(recurringStored, bundle.currency)}</span>
                      )}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
        <div className="space-y-4">
          {/* sell-a-bundle */}
          <h3 className="text-sm font-semibold text-gray-900">
            Sell a bundle
          </h3>
          <p className="text-xs text-gray-500">
            Type the monthly credit allowance and both prices, then activate the account by hand.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="monthly-credits" className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Monthly credits
              </label>
              <input
                id="monthly-credits"
                aria-label="Monthly credits"
                type="number"
                min="1"
                step="1"
                value={monthlyCredits}
                onChange={(e) => setMonthlyCredits(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="sale-currency" className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Currency
              </label>
              <select
                id="sale-currency"
                aria-label="Sale currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="USD">USD</option>
                <option value="ILS">ILS</option>
              </select>
            </div>
            <div>
              <label htmlFor="first-payment" className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                First payment
              </label>
              <div className="flex items-center gap-2">
                <span className="w-6 text-sm text-gray-500" aria-hidden="true">{currencySymbol(currency)}</span>
                <input
                  id="first-payment"
                  aria-label="First payment"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={initialAmount}
                  onChange={(e) => setInitialAmount(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div>
              <label htmlFor="recurring-monthly-payment" className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Recurring monthly payment
              </label>
              <div className="flex items-center gap-2">
                <span className="w-6 text-sm text-gray-500" aria-hidden="true">{currencySymbol(currency)}</span>
                <input
                  id="recurring-monthly-payment"
                  aria-label="Recurring monthly payment"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={recurringAmount}
                  onChange={(e) => setRecurringAmount(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          {bosModulePicker}

          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Sales notes (optional, internal only)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Agreed on the call with…"
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Saved on the bundle for the team — never shown to the customer.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <button
              onClick={handleManualActivate}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              <CheckCircleIcon className="h-4 w-4" />
              Activate manually
            </button>
            {bundle && (
              <button
                onClick={handleSaveChanges}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-primary-300 text-primary-700 text-sm font-medium hover:bg-primary-50 disabled:opacity-50"
              >
                Save changes
              </button>
            )}
          </div>
          {/* sell-a-bundle-end */}
        </div>
        )}

        {isActive && (
          <div className="border-t border-gray-200 pt-6 space-y-4">
            {bosModulePicker}
            <button
              type="button"
              onClick={handleSaveBosModules}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-primary-300 text-primary-700 text-sm font-medium hover:bg-primary-50 disabled:opacity-50"
            >
              Save BOS modules
            </button>
          </div>
        )}

        {/* Credits + lifecycle */}
        {bundle && (
          <div className="border-t border-gray-200 pt-6 space-y-4">
            <div className="space-y-4">
              <div>
                <GrantRow
                  label="Grant credits"
                  amountLabel="Credit grant amount"
                  cadenceLabel="Credit grant cadence"
                  amount={grantCredits}
                  cadence={creditCadence}
                  onAmount={setGrantCredits}
                  onCadence={setCreditCadence}
                  onSubmit={handleGrantCredits}
                  saving={saving}
                  icon={CurrencyDollarIcon}
                  balance={wallet?.topUpRemaining
                    ? `${Number(wallet.topUpRemaining).toLocaleString()} one-time credits left`
                    : 'No one-time credits'}
                  monthlyAmount={Number(wallet?.monthlyGrantCredits) || 0}
                  help={creditCadence === 'monthly'
                    ? 'Added this period and every later period, on top of the sold allowance. Unused grant resets with the allowance. The sold monthly credits stay as they are. 0 turns the grant off and replaces the previous amount.'
                    : 'Never expire, and are spent after the monthly allowance (sold credits plus any monthly grant). A grant already added can be changed or deleted below.'}
                />
                <OneTimeGrantList
                  noun="credits"
                  grants={(data?.oneTimeGrants?.credits || []).map((grant) => ({
                    id: grant.id,
                    amount: grant.credits,
                    remaining: null,
                    createdAt: grant.createdAt,
                    expiresAt: null,
                  }))}
                  saving={saving}
                  onSave={(grant, next) => handleChangeOneTimeGrant({
                    path: 'credits', field: 'credits', noun: 'credits', grant, next,
                  })}
                  onDelete={(grant) => handleDeleteOneTimeGrant({ path: 'credits', noun: 'credits', grant })}
                />
              </div>
              <div>
                <GrantRow
                  label="Grant call minutes"
                  amountLabel="Call minute grant amount"
                  cadenceLabel="Call minute grant cadence"
                  amount={grantMinutes}
                  cadence={minuteCadence}
                  onAmount={setGrantMinutes}
                  onCadence={setMinuteCadence}
                  onSubmit={handleGrantMinutes}
                  saving={saving}
                  icon={PhoneIcon}
                  balance={data?.voiceMinutes
                    ? `${Number(data.voiceMinutes.remaining || 0).toLocaleString()} minutes left`
                    : 'Minutes unavailable'}
                  monthlyAmount={Number(data?.voiceMinutes?.minutesPerMonth) || 0}
                  help={minuteCadence === 'monthly'
                    ? 'This month is granted now. Later months are granted on the 1st (Asia/Jerusalem) and expire at the end of that month. Saving a new amount does not add a second pack for a month already granted. 0 stops future months.'
                    : 'Last 12 months, the same as a one-time purchase. A grant already added can be changed or deleted below.'}
                />
                <OneTimeGrantList
                  noun="minutes"
                  grants={(data?.oneTimeGrants?.minutes || []).map((grant) => ({
                    id: grant.id,
                    amount: grant.totalMinutes,
                    remaining: grant.remainingMinutes,
                    createdAt: grant.purchasedAt,
                    expiresAt: grant.expiresAt,
                  }))}
                  saving={saving}
                  onSave={(grant, next) => handleChangeOneTimeGrant({
                    path: 'voice-minutes', field: 'minutes', noun: 'minutes', grant, next,
                  })}
                  onDelete={(grant) => handleDeleteOneTimeGrant({ path: 'voice-minutes', noun: 'minutes', grant })}
                />
              </div>
              <div>
                <GrantRow
                  label="Grant WhatsApp templates"
                  amountLabel="WhatsApp template grant amount"
                  cadenceLabel="WhatsApp template grant cadence"
                  amount={grantTemplates}
                  cadence={templateCadence}
                  onAmount={setGrantTemplates}
                  onCadence={setTemplateCadence}
                  onSubmit={handleGrantTemplates}
                  saving={saving}
                  icon={ChatBubbleLeftRightIcon}
                  balance={data?.templateCredits
                    ? `${Number(data.templateCredits.remaining || 0).toLocaleString()} template credits left`
                    : 'Template credits unavailable'}
                  monthlyAmount={Number(data?.templateCredits?.creditsPerMonth) || 0}
                  help={templateCadence === 'monthly'
                    ? 'This month is granted now. Later months are granted on the 1st (Asia/Jerusalem) and expire at the end of that month. Saving a new amount does not add a second pack for a month already granted. 0 stops future months.'
                    : 'Last 12 months, the same as a one-time purchase. A grant already added can be changed or deleted below.'}
                />
                <OneTimeGrantList
                  noun="templates"
                  grants={(data?.oneTimeGrants?.templates || []).map((grant) => ({
                    id: grant.id,
                    amount: grant.totalCredits,
                    remaining: grant.remainingCredits,
                    createdAt: grant.purchasedAt,
                    expiresAt: grant.expiresAt,
                  }))}
                  saving={saving}
                  onSave={(grant, next) => handleChangeOneTimeGrant({
                    path: 'whatsapp-templates', field: 'credits', noun: 'templates', grant, next,
                  })}
                  onDelete={(grant) => handleDeleteOneTimeGrant({ path: 'whatsapp-templates', noun: 'templates', grant })}
                />
              </div>
            </div>

            {data?.usageBreakdown?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                  Credit usage this period
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.usageBreakdown.map((row) => (
                    <span
                      key={row.action_key}
                      className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs"
                    >
                      {row.action_key}: <strong>{row.credits}</strong> ({row.occurrences}×)
                    </span>
                  ))}
                </div>
              </div>
            )}

            {data?.account?.accessSuspended && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Access is suspended. People on this account cannot sign in. The bundle, WhatsApp number, sites, and data are unchanged.
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-2">
              {data?.account?.accessSuspended ? (
                <button
                  onClick={() => handleSuspendAccess(false)}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-amber-300 text-amber-800 text-sm hover:bg-amber-50 disabled:opacity-50"
                >
                  Restore access
                </button>
              ) : (
                <button
                  onClick={() => handleSuspendAccess(true)}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-amber-300 text-amber-800 text-sm hover:bg-amber-50 disabled:opacity-50"
                >
                  <NoSymbolIcon className="h-4 w-4" />
                  Suspend access
                </button>
              )}
              {isActive && !bundle.cancel_at_period_end && (
                <button
                  onClick={() => handleCancel(true)}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel at period end
                </button>
              )}
              {bundle.status !== 'canceled' && (
                <button
                  onClick={() => handleCancel(false)}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-sm hover:bg-red-50 disabled:opacity-50"
                >
                  <XCircleIcon className="h-4 w-4" />
                  Cancel immediately
                </button>
              )}
              <button
                onClick={handleRevert}
                disabled={saving}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-500 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Revert to legacy billing
              </button>
              <button
                onClick={handleRestoreEnterprise}
                disabled={saving}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-indigo-300 text-indigo-700 text-sm hover:bg-indigo-50 disabled:opacity-50"
              >
                Restore Enterprise / Agency
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BundleSection;
